import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Statistic, Table, Space, Tag, Tooltip, message } from 'antd';
import { DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { paymentService, Payment, PaymentFilters } from '../../services/paymentService';
import { FilterValues } from '../../components/TableFilter';
import { PaymentFilters as PaymentFiltersComponent } from 'components/PaymentFilters/PaymentFilters';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'COMPLETED':
    case 'COMPLETED_AND_CONSUMED':
    case 'REFUND_COMPLETED':
      return 'green';
    case 'PENDING':
    case 'COMPLETED_WITH_ERROR':
      return 'orange';
    case 'FAILED':
    case 'REFUND_FAILED':
      return 'red';
    case 'CANCELED':
    case 'ABORTED':
      return 'default';
    case 'INITIALED':
      return 'blue';
    case 'REQUESTED':
    case 'REFUND_REQUESTED':
      return 'cyan';
    case 'REFUNDED':
      return 'purple';
    default:
      return 'default';
  }
};

const getMethodColor = (method: string) => {
  switch (method) {
    case 'CARD': return 'blue';
    case 'TRANSFER': return 'purple';
    case 'CASH': return 'gold';
    default: return 'default';
  }
};

const Transactions: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState({ totalAmount: 0, successCount: 0, failedCount: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<FilterValues>({});
  const [numberRangeFilters, setNumberRangeFilters] = useState<{
    amount?: [number | undefined, number | undefined];
  }>({});

  const mapFiltersToApi = (): PaymentFilters => ({
    amountMin: numberRangeFilters.amount?.[0],
    amountMax: numberRangeFilters.amount?.[1],
    method: filters.method,
    status: filters.status,
    description: filters.description,
    agencyName: filters.agencyName,
    dateMin: filters.dateMin,
    dateMax: filters.dateMax,
  });

  const fetchPayments = useCallback(async (page = 1, size = pageSizeState) => {
    try {
      setLoading(true);
      const apiFilters = mapFiltersToApi();
      const response = await paymentService.getAllAdmin({ page: page - 1, size, filters: apiFilters });
      setPayments(response.data.content || []);
      setTotal(response.data.totalElements || 0);
      setCurrentPage(page);
      setPageSizeState(size);
    } catch (e) {
      message.error(t('admin.transactions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filters, numberRangeFilters, pageSizeState]);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [fetchPayments]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await paymentService.getStats();
      setStats(response.data);
    } catch (e) {
      message.error(t('admin.transactions.statsLoadError'));
    }
  }, []);

  const handleClearFilters = () => {
    setFilters({});
    setNumberRangeFilters({});
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '10%', render: (id: number) => `#${id}` },
    { 
      title: t('common.amount'), dataIndex: 'amount', key: 'amount',
      render: (amount: number, record: Payment) => (
        <Space>
          <DollarOutlined style={{ color: '#1890ff' }} />
          <span>{amount} {'€'}</span>
        </Space>
      )
    },
    { 
      title: t('common.date'), dataIndex: 'date', key: 'date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <span>{new Date(date).toLocaleDateString()}</span>
        </Space>
      )
    },
    { title: t('common.method'), dataIndex: 'method', key: 'method', render: (method: string) => <Tag color={getMethodColor(method)}>{method}</Tag> },
    { title: t('common.status'), dataIndex: 'status', key: 'status', render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag> },
    { title: t('common.description'), dataIndex: 'description', key: 'description', ellipsis: true, render: (desc: string) => <Tooltip title={desc}><span>{desc || '/'}</span></Tooltip> },
    { title: t('common.agencyName'), dataIndex: 'agencyName', key: 'agencyName' }
  ];

  return (
    <>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic title={t('admin.transactions.totalAmount')} value={stats.totalAmount} prefix={<DollarOutlined />} suffix="€" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title={t('admin.transactions.successfulPayments')} value={stats.successCount} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title={t('admin.transactions.failedPayments')} value={stats.failedCount} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <PaymentFiltersComponent
        filters={filters}
        numberRangeFilters={numberRangeFilters}
        setFilters={setFilters}
        setNumberRangeFilters={setNumberRangeFilters}
        onClearAll={handleClearFilters}
      />

      <div style={{ marginTop: 24 }}>
        <Card title={t('admin.transactions.listTitle')}>
          <Table
            columns={columns}
            dataSource={payments}
            loading={loading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSizeState,
              total,
              showSizeChanger: true,
              onChange: (page, size) => fetchPayments(page, size)
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default Transactions;
